import React, { useRef, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { server } from "../app/consts";

export interface PaymentDetails {
  sum: string;
  currency?: string | number;
  contact?: string;
  email?: string;
  phone?: string;
  cred_type?: string | number;
  tranmode?: string;
  lang?: string;
  uid?: string | null;
  workoutId?: string;
}

interface TranzilaPaymentModalProps {
  open: boolean;
  onClose: () => void;
  terminalName?: string;
  paymentDetails?: PaymentDetails;
  reservationTimeoutMinutes?: number;
}

const TranzilaPaymentModal: React.FC<TranzilaPaymentModalProps> = ({
  open,
  onClose,
  terminalName = "fxpramkliot",
  paymentDetails,
  reservationTimeoutMinutes = 15,
}) => {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // מצב עבור הטיימר (בשניות)
  const totalSeconds = reservationTimeoutMinutes * 60;
  const [timeLeft, setTimeLeft] = useState<number>(totalSeconds);

  const workoutId = paymentDetails?.workoutId || "";
  const uid = paymentDetails?.uid || "";
  const originParam = encodeURIComponent(window.location.origin);

  const apiBase = server.startsWith("http")
    ? server.replace(/\/+$/, "")
    : `${window.location.origin}${server}`.replace(/\/+$/, "");

  const formData: Record<string, string> = {
    supplier: terminalName,
    sum: paymentDetails?.sum || "0",
    currency: "1",
    contact: paymentDetails?.contact || "",
    email: paymentDetails?.email || "",
    phone: paymentDetails?.phone || "",
    cred_type: "1",
    tranmode: "A",

    success_url_address: `${apiBase}/tranzila-success/?workout_id=${workoutId}&uid=${uid}&origin=${originParam}`,
    fail_url_address: `${apiBase}/tranzila-fail/?workout_id=${workoutId}&uid=${uid}&origin=${originParam}`,
    notify_url_address: `${apiBase}/tranzila-handler/?workout_id=${workoutId}&uid=${uid}&origin=${originParam}`,

    lang: "il",
    bit_pay: "1",
    tranzila_auto_redirect: "1",
    redirect_type: "1",
  };

  // אתחול והפעלת טיימר ספירה לאחור
  useEffect(() => {
    if (!open) return;

    // איפוס הזמן בכל פעם שהמודל נפתח
    setTimeLeft(totalSeconds);

    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          onClose();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [open, totalSeconds, onClose]);

  // שליחת טופס ל-iframe
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        if (formRef.current) {
          formRef.current.submit();
        }
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [open]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            minHeight: "640px",
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            תשלום מאובטח
          </Typography>
        </Box>

        {/* טיימר שמירת המקום */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.8,
            backgroundColor: timeLeft < 120 ? "#ffebee" : "#f5f5f5",
            color: timeLeft < 120 ? "#d32f2f" : "#424242",
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: timeLeft < 120 ? "#ef9a9a" : "#e0e0e0",
            transition: "all 0.3s ease",
          }}
        >
          <AccessTimeIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600, direction: "ltr" }}>
            {formatTime(timeLeft)}
          </Typography>
          <Typography variant="caption" sx={{ display: { xs: "none", sm: "inline" } }}>
            שמירת מקום
          </Typography>
        </Box>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: "relative", height: "580px" }}>
        <form
          ref={formRef}
          action={`https://direct.tranzila.com/${terminalName}/iframenew.php`}
          method="POST"
          target="tranzila-payment-frame"
          style={{ display: "none" }}
        >
          {Object.entries(formData).map(([key, value]) => (
            <input key={key} type="hidden" name={key} value={value} />
          ))}
        </form>

        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              backgroundColor: "background.paper",
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              טוען מסוף תשלום מאובטח...
            </Typography>
          </Box>
        )}

        <Box sx={{ p: 1, backgroundColor: "#fff8e1", borderBottom: "1px solid #ffe082" }}>
          <Typography
            variant="body2"
            sx={{
              color: "#d32f2f",
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "13px",
            }}
          >
            שימו לב: כרגע לא ניתן לשלם באמצעות כרטיסי אמריקן אקספרס ודיינרס.
          </Typography>
        </Box>

        <iframe
          id="tranzila-payment-frame"
          name="tranzila-payment-frame"
          title="Tranzila Secure Payment Frame"
          onLoad={() => setIsLoading(false)}
          allow="payment"
          style={{
            width: "100%",
            height: "calc(100% - 37px)",
            border: "none",
            display: "block",
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default TranzilaPaymentModal;